import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, Plus } from "lucide-react";
import { clsx } from "clsx";
import { Input, usePopover } from "../ui";
import { useSession } from "./session";

/** Workspace switcher (spec §9.27, level `standard`): per device (I-7); the id is shown nowhere. */
export function WorkspaceSwitch() {
  const { t } = useTranslation();
  const session = useSession((s) => s.session);
  const workspaceId = useSession((s) => s.workspaceId);
  const select = useSession((s) => s.selectWorkspace);
  const load = useSession((s) => s.load);
  const { open, toggle, setOpen, ref } = usePopover<HTMLDivElement>();
  const [name, setName] = useState("");
  const list = session?.workspaces ?? [];
  const current = list.find((w) => w.id === workspaceId);
  if (!current) return null;
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={toggle}
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid="workspace-switch"
        className="inline-flex h-[30px] items-center gap-1 rounded-sm border border-neutral-200 bg-[var(--surface)] px-2 text-[13px] hover:border-neutral-700 dark:border-neutral-800"
      >
        {current.name} <ChevronDown size={13} aria-hidden />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-9 z-40 w-60 rounded-md border border-neutral-200 bg-[var(--surface)] p-1 text-sm shadow-[var(--shadow-md)] dark:border-neutral-700"
        >
          {list.map((w) => (
            <button
              key={w.id}
              type="button"
              role="menuitem"
              className={clsx(
                "block w-full truncate rounded-sm px-2 py-1 text-left hover:bg-neutral-100 dark:hover:bg-neutral-800",
                w.id === workspaceId && "bg-neutral-100 dark:bg-neutral-800",
              )}
              onClick={() => {
                select(w.id);
                setOpen(false);
              }}
            >
              {w.name}
            </button>
          ))}
          <form
            className="mt-1 flex items-center gap-1 border-t border-neutral-200 pt-1 dark:border-neutral-800"
            onSubmit={async (e) => {
              e.preventDefault();
              if (!name.trim()) return;
              const res = await fetch("/api/internal/workspaces", {
                method: "POST",
                credentials: "same-origin",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ name: name.trim() }),
              });
              if (res.ok) {
                const body = (await res.json()) as { workspace: { id: string } };
                await load();
                select(body.workspace.id);
                setName("");
                setOpen(false);
              }
            }}
          >
            <Plus size={14} aria-hidden className="ml-1 text-neutral-400" />
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              // in a circle, shared workspaces are made in Settings → Circle; this one is the person's own
              placeholder={session?.mode === "circle" ? t("workspaces.newOwn") : t("workspaces.new")}
              aria-label={session?.mode === "circle" ? t("workspaces.newOwn") : t("workspaces.new")}
              className="h-[28px] py-0 text-[12.5px]"
              data-testid="workspace-new"
            />
          </form>
        </div>
      )}
    </div>
  );
}
